/**
 * Combined changelog + feature spotlight. Newest entries first.
 *
 * Every entry appears on the Changelog page (?changelog). Entries with
 * `spotlight: false` are changelog-only; all others ALSO show as a one-time
 * popup for returning users (tracked in prefs via seenUpdates). New visitors
 * who see the Welcome modal skip the popup.
 *
 * id:        unique string (never reuse — it's the seen-tracking key)
 * date:      display date string
 * emoji:     visual icon
 * title:     short headline
 * body:      one-liner description (the popup + changelog both show this)
 * link:      optional URL (e.g. ?players)
 * external:  set true when `link` leaves the site — opens in a new tab instead of
 *            navigating the reader away mid-changelog.
 * spotlight: set to false for changelog-only (no popup). Default = true.
 *            Every unseen spotlight entry pops for a returning visitor, so
 *            backfilled history should be changelog-only — otherwise catching the
 *            feed up buries people in notices for things they saw weeks ago.
 */
export const SITE_UPDATES = [
  {
    id: 'round-win-rates-2026-10',
    date: 'Oct 22, 2026',
    emoji: '🎯',
    title: 'Win rate by round',
    body: 'A player profile now shows their record in each round: quarterfinal, semifinal, final, the champion match as the challenger, and title defenses as the champion. Each round is marked against the site-wide rate, since later rounds only face teams that won their way there (challengers beat the champion about 41% of the time). The 📊 Boards tab ranks the best record in each round.',
    link: '?players&tab=boards',
  },
  {
    id: 'bet-history-2026-10',
    date: 'Oct 21, 2026',
    emoji: '🧾',
    title: 'Every bet you ever placed, on your profile',
    body: 'The 💰 Gambling tab on a player profile now lists every bet they have placed: when, which match, which team, how much, the closing odds, what it won or lost, and their gil at the time. Filter by result, round, team, year or bet size, and ⚔️ on any bet opens that fight in its bracket. Odds come from the closing pot, since FFTBG doesn\'t keep the odds at the moment of a bet.',
    link: '?players',
  },
  {
    id: 'mods-monster-picker-2026-10',
    date: 'Oct 2, 2026',
    emoji: '🐉',
    title: 'My Mods: a class picker, and monster picks that know your cooldown',
    body: '"Suggest for" in ⚡ My Mods is now a grid of portraits instead of a long dropdown. ✦ marks the classes that earn your class bonus, and each class has ♂/♀ buttons, so "female Lancer" is one click. Picking 🐉 Monster used to just say !fight. Now it suggests the highest-win-rate monster you own, can afford, and can actually roll. Strong and elite monsters share a cooldown per tier: field one elite and every elite waits it out. My Mods reads that from your skill list, the same way the bot\'s skill modifiers show it, and lists the rest of your monsters under "all options". Turn on "Respect Monster Quest" and it suggests only the monsters your next Monster Quest set still needs. The VIP helper now skips monsters on cooldown too.',
  },
  {
    id: 'loser-quest-2026-10',
    date: 'Oct 2, 2026',
    emoji: '🕊️',
    title: 'The Miku Quest',
    body: 'Named for MikuShikhu, who volunteered. It\'s the Everything Quest in reverse: lose with every class, all 20 human jobs and all 55 monsters, once per round. 🚪 First-Round Exit is a quarterfinal loss, 💔 Semifinal Heartbreak a semi, 🥈 So Close the final, and 💐 Always the Bridesmaid means winning the final and then losing the title match. 85 people have finished First-Round Exit, and only Drunkpak has finished Always the Bridesmaid. Six more are one class short, including Q99m, who has lost the title match with every monster at least four times but never with a Thief. Find it on the Players page\'s Quests tab, and your own four checklists on your profile\'s Achievements tab. Completions show up in the "Just happened" ticker too.',
    link: '?players&tab=quests&quest=loser',
  },
  {
    id: 'vip-helper-2026-10',
    date: 'Oct 1, 2026',
    emoji: '🎖️',
    title: 'My Mods: "Help me with VIP quest"',
    body: 'A new switch in ⭐ My entry (⚡ My Mods on the bracket) aims your entry at the VIP Quest: a class you haven\'t champed this season, picked for its edge on the champion you\'d face. Strong and elite monsters you own and still need come first, since they share a cooldown. After those it weighs each class\'s win rate against what the champion is weak to or shrugs off: a Bomb\'s Fire into their Treant, say, but no Faith magic into a Steel Giant. It can also force a skillset you own that brings the right element. Every pick says why, and a 🎲 button runs the battle sim for the top few against the champion.',
  },
  {
    id: 'vip-board-2026-10',
    date: 'Oct 1, 2026',
    emoji: '🎖️',
    title: 'The VIP Quest board',
    body: 'Who has champed the most different classes this season? The new 🎖️ VIP Quest page (in the Champions menu) ranks everyone. Gender doesn\'t matter, monsters count, and there are 75 to collect. Click a player for their checklist of all 75, switch to humans, monsters or ♂/♀ humans, or look back at any past season. Set your username in ⚙ Settings and your own row is pinned at the top. The Seasons page shows each season\'s top five too.',
    link: '?vip',
  },
  {
    id: 'mods-hidden-2026-10',
    date: 'Oct 1, 2026',
    emoji: '💎',
    title: 'My Mods: "Save my hidden skills"',
    body: 'For the hoarders: if you own a hidden skill, ⚡ My Mods on the bracket now has a "Save my hidden skills" switch. With it on, no suggestion forces Math Skill, Equip Perfume, Teleport 2 or Blade Grasp. That covers Go big, ⭐ My entry and the picks you can adjust. It\'s off until you turn it on, and it remembers your choice.',
  },
  {
    id: 'mods-offcd-2026-09',
    date: 'Sep 29, 2026',
    emoji: '⭐',
    title: 'My entry: "off cooldown"',
    body: 'Another one from Taobakaa: "Force my best skill" in ⭐ My entry (⚡ My Mods on the bracket) has a third choice between "any usable" and "no penalty". "Off cooldown" takes your best skill that has no reroll tildes, so a win pays full EXP, but it will take an uncommon or rare skill and its priority cost. That\'s the one for quiet hours, when priority matters less. A Ninja with Mana Shield and Retreat both off cooldown now gets Mana Shield, where "no penalty" stops at the common pick. Thanks again, Taobakaa!',
  },
  {
    id: 'ticker-2026-09',
    date: 'Sep 28, 2026',
    emoji: '🎉',
    title: '"Just happened," on the bracket page',
    body: 'A strip under the nav now scrolls the past week\'s milestones: first titles and 10th, 50th and 100th ones, quest completions, champion teams reaching 10 or more tournaments, titles won as a raid boss, and big gifting milestones. It updates after every tournament, pauses when you hover over it, and holds still if your device asks for reduced motion. Each line links to the player. If it isn\'t for you, switch it off in ⚙ Settings.',
  },
  {
    id: 'gift-quest-2026-09',
    date: 'Sep 28, 2026',
    emoji: '🎁',
    title: 'The Gift Quest',
    body: 'A new quest on the Quests page: gift every skill in the game at least once, to anyone. It counts the bot\'s own gift confirmations back to February 2020 against FFTBG\'s own list of 186 skills. Nobody has finished it yet: DarrenDinosaurs and Antipathics are tied at 167. Your checklist, grouped by skill type, is on the Achievements tab of your profile. If a skill on the list can\'t actually be gifted, tell Luxor and it comes off. Building it also turned up 370 gifts the Gifts page had been missing (skills with a hyphen in the name, like N-KaiArmlet), so a few of those counts went up.',
    link: '?players&tab=quests&quest=gift',
  },
  {
    id: 'my-entry-2026-09',
    date: 'Sep 25, 2026',
    emoji: '⭐',
    title: 'My Mods remembers your class, and builds your entry',
    body: 'Two ideas from Taobakaa on stream, now in ⚡ My Mods on the bracket (it appears once your username is set in ⚙ Settings). The class you pick under "Suggest for" is remembered, so if you always force Lancer it opens on Lancer. And ⭐ My entry builds your !fight command from rules you set once: force your best owned skill for that class (from any usable skill, or only ones with no penalty: no reroll tildes and no priority cost), ban the class\'s worst roll, bribe out GilgameHeart. It fits all of that into FFTBG\'s four-parameter limit and says what it had to leave out, so opening My Mods puts your command one click from copied. Luxor added one more: tick "Respect my quest" and it enters as something your pinned quest still needs, like a male Lancer for the Human Gendered quest, or a monster you own for the Monster Quest. Thanks, Taobakaa!',
  },
  {
    id: 'gifts-2026-09',
    date: 'Sep 16, 2026',
    emoji: '🎁',
    title: 'Who has gifted the most',
    body: 'A new page in the Chat menu, built off a request in Discord: every !gift the bot has ever confirmed — 44,771 of them since February 2020 — ranked by giver, by recipient, by skill, and by the giver→recipient pairs that keep recurring, with a live feed of the latest. Nhammen leads at 2,546. It counts only the bot\'s own confirmation, not who typed the command, because !gift fails when you are broke, when it fires before the raffle, or when it is mashed three times in four seconds — which meant reading the bot\'s own message log rather than viewer chat, which never records the bot at all. Your own given and received counts are on the Chat tab of your profile.',
    link: '?gifts',
  },
  {
    id: 'season-units-2026-08',
    date: 'Aug 11, 2026',
    emoji: '🎲',
    title: 'Season Units — what actually got rolled',
    body: 'A new page in the Champions menu, ranking all 75 classes by how often the generator actually rolled them in a season, rarest first. Season 28\'s rarest was King Behemoth at 253 rolls, against Knight\'s 6,098. Every row carries its share of the season, how that share moved against the season before, how many different people got one, match win rate and titles — and clicking a row opens the ten people who rolled it most, both that season and all-time. The ⚥ toggle splits each human class into ♂/♀ rows, which is where it gets interesting: the rarest human roll of Season 28 was a male Oracle. This counts rolls, not appearances — a unit that reaches the final is still one roll, which is what makes "least rolled" a fair question at all.',
    link: '?units',
  },
  {
    id: 'clockwork-coliseum-domain-2026-08',
    date: 'Aug 11, 2026',
    emoji: '⚙️',
    title: 'Clockwork Coliseum has a real address',
    body: 'The web auto-battler we build alongside this site now lives at clockworkcoliseum.com, and the promo under the Champ Match cell points there. Queue any time — including mid-match — and watch your team fight it out. Same beta, same crew, still free.',
    link: 'https://clockworkcoliseum.com',
    external: true,
  },
  {
    id: 'season-29-2026-08',
    date: 'Aug 9, 2026',
    emoji: '👑',
    title: 'Season 29 has begun',
    body: 'ToC28 went to the eighth seed: LuckyMousey, Conker63, Arbot and Stone Achromous took the Champion of Champions crown off a team that had held it through five straight Tournaments of Champions. The whole site understands Season 29 now — season stats, betting, chat and championship history all bucket into it. Two fixes came with it. The Champions page used to go blank in the window between a ToC and the new season\'s first dethroning, hiding the season picker along with everything else; it now still lets you browse back through every past season. And the line naming each season\'s ToC winner was quietly showing that season\'s top seed instead — wrong about four times in five, since the top seed has won only 4 of the 22 seeded ToCs. It now names who actually won the bracket, whether they went on to take the crown, and links you to it.',
    link: '?champions',
  },
  {
    id: 'champ-history-zodiac-quest-2026-08',
    date: 'Aug 9, 2026',
    emoji: '♎',
    title: 'Zodiac quests in your championship history',
    body: 'The loadout picker on your profile\'s history tab has a Zodiac group now, so "which classes have I champed with a Libra unit?" gets the same class checklist the secondary, support and gear filters give you. Because a unit\'s sign comes from the reign itself rather than the loadout files, this one works for every champion instead of only the ~400 with 50+ titles — if that picker never showed up for you before, it does now.',
    spotlight: false,
    link: '?player=LuxorDoesntFrame&tab=history',
  },
  {
    id: 'seasons-page-2026-07',
    date: 'Jul 14, 2026',
    emoji: '📅',
    title: 'Seasons — FFTBG era by era',
    body: 'A page for the shape of FFTBG over time: every season since 2019, each one ending in its Tournament of Champions. The grid view puts all of them side by side with class-meta drift sparklines; picking a season gives you its champions and win rates, its records, its biggest gambling winners, its loudest chatters, the emotes that were born in it, and that season\'s rookies. There is a per-player view too, showing a career as an arc across seasons, plus a titles-by-season strip on every profile.',
    spotlight: false,
    link: '?seasons',
  },
  {
    id: 'raid-boss-quest-2026-07',
    date: 'Jul 10, 2026',
    emoji: '👹',
    title: 'Raid Boss Quest & leaderboard',
    body: 'Raid bosses — the buffed units that occasionally roll onto a challenger team — now have their own board: who has fielded the most, who has actually won titles with one, and at what rate. There is a quest attached, champing all 20 human jobs as a raid boss. Nobody has finished it; the leader sits at 11 of 20.',
    spotlight: false,
    link: '?players&tab=boards',
  },
  {
    id: 'mighty-sword-rework-2026-07',
    date: 'Jul 10, 2026',
    emoji: '⚔️',
    title: 'The Mighty Sword rework, modelled',
    body: 'FFTBG reworked the Limit skillset, and the damage engine caught up. The four Mighty Sword skills moved from PA*WP to PA*(WP+N) with an MP cost; Unyielding Cut arrived with a two-branch conditional — it reads whether the target is armed and swings differently — which needed a formula family that keys off the defender\'s stats, a first for the parser. Siphon now drains a percentage of the target\'s MP rather than acting like a max-HP nuke. Card damage, the fight view, the Builder and the battle sim all reflect the new numbers.',
    spotlight: false,
  },
  {
    id: 'grade-letters-rework-2026-07',
    date: 'Jul 8, 2026',
    emoji: '🎓',
    title: 'Unit grades that actually spread out',
    body: 'The letter on a unit card used to be a fraction of a theoretical ceiling, which crushed roughly 87% of human units into C+ or C — technically defensible and completely useless for telling two rolls apart. A human\'s letter is now its percentile among real rolls of the same class and gender, so an A genuinely means "top ~11% roll for this class". Monsters were re-fitted too: their grade follows a sample-weighted trend line through their Brave/Faith win curves at the unit\'s exact values, instead of snapping to a bucket — which had produced the odd result of an 82-Brave Tiamat grading below a 79-Brave one.',
    spotlight: false,
  },
  {
    id: 'class-index-gender-2026-07',
    date: 'Jul 8, 2026',
    emoji: '⚥',
    title: 'Class & Monster Index, split by gender',
    body: 'A ⚥ toggle on the Stats page\'s Class & Monster Index breaks every human class into its own ♂ and ♀ rows — 93 of them — with each column recomputed for that gender rather than pooled. Sorting by win rate then ranks them as separate entries, which is the only way to see the gaps that the combined view averages away.',
    spotlight: false,
    link: '?stats&cisplit=1',
  },
  {
    id: 'sprite-weapons-2026-07',
    date: 'Jul 6, 2026',
    emoji: '🗡️',
    title: 'Units swing their actual weapons',
    body: 'The sprite gallery can now play a unit\'s attack animation with its real equipped weapon composited on top — the long-standing hard part, since the game stores the weapon\'s position, rotation and scale per animation frame in a format that had to be reverse-engineered against emulator memory and GPU packets to get right. Every hand weapon in FFTBG except knives and thrown items now animates, including the ones the Arena added that have no original art and borrow from the same family.',
    spotlight: false,
    link: '?gallery',
  },
  {
    id: 'bracket-attack-anims-2026-07',
    date: 'Jul 7, 2026',
    emoji: '💥',
    title: 'Live-match units attack on the bracket',
    body: 'Units in the current match occasionally break out of their idle animation and play a one-shot attack with the weapon they are actually holding. There is a toggle in Settings if you would rather they stood still, and it respects your system\'s reduced-motion preference.',
    spotlight: false,
  },
  {
    id: 'game-data-source-2026-07',
    date: 'Jul 7, 2026',
    emoji: '📊',
    title: 'Class, item and ability data straight from FFTBG',
    body: 'The class stats, item stats, ability text and tooltips behind every card and damage estimate now come from FFTBG\'s own published help files rather than a third-party mirror that had drifted. The parser refuses to write a snapshot it does not fully understand, so a game update that changes the format fails loudly instead of quietly poisoning the numbers. Tooltips are bundled now too, so the bracket no longer waits on a live fetch to show them.',
    spotlight: false,
  },
  {
    id: 'carpet-bible-2026-07',
    date: 'Jul 4, 2026',
    emoji: '📖',
    title: 'The Holy Carpet Bible & the Technospeak wheel',
    body: 'Two additions to the Chat menu on the sillier end. The Carpet Bible collects Technominari\'s ~13,000 long-form chat verses, sorted into Canon, Gospel and Apocrypha. The Technospeak wheel is a faithful, dependency-free port of the garish church-word spinner. Both are exactly as serious as they sound.',
    spotlight: false,
    link: '?carpet',
  },
  {
    id: 'classes-page-2026-07',
    date: 'Jul 3, 2026',
    emoji: '🎭',
    title: 'A page for every class and monster',
    body: 'Default stats for every job and monster — 20 human classes and 55 monsters — with animated walking sprites and a live unit card showing what a stock example of that class actually looks like. Each one carries a strategy section built from real win rates — the best and worst secondary, support, reaction, move and accessory picks for that class, and for monsters, where it sits in the rotation.',
    spotlight: false,
    link: '?classes',
  },
  {
    id: 'ability-breakdown-2026-07',
    date: 'Jul 2, 2026',
    emoji: '🧮',
    title: 'Show your work: full ability breakdowns',
    body: 'Clicking an ability on a unit card now pins a breakdown showing exactly how the number was reached — the formula, then the same formula with the real values substituted in, then every modifier applied in order. It covers both teams rather than just enemies, handles drain and monster skills, and marks the riders that come along with a hit. Basic attacks got the same treatment on the weapon tooltip, and monster innate elemental affinities — including outright immunities — finally show up.',
    spotlight: false,
  },
  {
    id: 'champ-history-filters-2026-07',
    date: 'Jul 26, 2026',
    emoji: '👑',
    title: 'Champion history: new filters, champion numbers, and bracket links',
    body: 'The championship history on your profile can now be sliced by the zodiac sign and gender of the unit you champed with, by team color, by ToC season, and by how long the reign lasted — on top of the class and loadout filters that were already there. You can also sort by zodiac, or by the Brave and Faith you held the belt at. Every reign now shows its place in the lineage — "#27,503", the 27,503rd champion in FFTBG history — and links straight to two brackets: the one you won the belt in, and your first defense. (Filters deep-link, so a filtered list is shareable.)',
    link: '?player=LuxorDoesntFrame&tab=history',
  },
  {
    id: 'best-worst-teams-2026-06',
    date: 'Jun 30, 2026',
    emoji: '🏅',
    title: 'Best & Worst Teams of all time',
    body: 'A new leaderboard in the Champions menu: every 8-team bracket in FFTBG history — 519k teams across 65k tournaments — graded by the same rating the unit cards use, ranked top and bottom 250, both all-time and for the modern era. There is also a Worst Champs board: the flukiest title-winners ever, like an F-graded squad that took the crown with a Swine and a Floating Eye on it. Each team lists its full roster and links straight to that tournament\'s bracket. (The grade reads through today\'s class/item data, so the all-time board is a present-day-lens estimate — the Modern board is the fair like-for-like cut.)',
    link: '?teams',
  },
  {
    id: 'cards-and-team-popup-2026-06',
    date: 'Jun 30, 2026',
    emoji: '🎴',
    title: 'Sharper cards & a richer team-rating popup',
    body: 'The team-grade hover popup got a redesign — each unit now shows its class, a diverging win-rate bar, and the skills and stats that helped or hurt its grade, in a clean readable font. Two card fixes rode along: a stat now turns red when it is below its class average (it used to read green either way), and Attack Up / Magic Attack Up / Strengthen finally factor into the basic-attack damage shown on the card — they already did in the fight view and the sim.',
    spotlight: false,
  },
  {
    id: 'carried-by-monsters-2026-06',
    date: 'Jun 28, 2026',
    emoji: '🤝',
    title: 'Carried by Monsters — a new quest',
    body: 'Monster Quest, flipped on its head: a new board under Players ▸ 🏅 Quests tracking every monster type a teammate fielded on a title team you won — the monsters that carried YOU, not the ones you piloted. It gets the full Monster-Quest treatment: completion-coin badges with "Nth-ever" ranks, an Elite/Strong checklist, and a celebration view listing every assist by date. (Zanbamon leads — carried by all 55 monster types four times over, while personally championing only 20 of them.) The same progress shows on every player profile. And the three separate gendered quests are now one Human Gendered board with ♂ / ♀ sub-tabs.',
    link: '?players&tab=quests&quest=carried',
  },
  {
    id: 'sim-fidelity-2-2026-06',
    date: 'Jun 27, 2026',
    emoji: '⚔️',
    title: 'More battle-sim fidelity',
    body: 'Another round of fixes to the Monte-Carlo simulator behind every win-%. Summons and other ranged magic now respect their cast range and can\'t be side-stepped like a melee swing; Draw Out fills the whole field — allies, enemies and the caster alike; and Hamedo only pre-empts an attacker actually in range, never reacting to a monster\'s swing unless it\'s Berserk. Dual Wield lands its second strike for bare-fisted and innate dualists too, units now path around walls and void instead of freezing on maze maps (with Fly/Teleport gliding over gaps), and a batch of statuses, songs & dances, Fury, cleanses and Math Skill all resolve more faithfully — every change still gated against thousands of historical brackets.',
    link: '?mechanics',
    spotlight: false,
  },
  {
    id: 'builder-from-bracket-2026-06',
    date: 'Jun 26, 2026',
    emoji: '✏️',
    title: 'Send any bracket unit to the Builder',
    body: 'Every unit on the bracket now has an ✏️ button that drops it straight into the Character Builder, fully loaded — tweak it, regrade it, or grab its share link. The Builder\'s force/ban panel also got smarter: a proper class picker, a GilgameHeart toggle, and ban options that understand when two share a slot.',
    link: '?build',
    spotlight: false,
  },
  {
    id: 'copypasta-page-2026-06',
    date: 'Jun 25, 2026',
    emoji: '🗯️',
    title: 'A dedicated copypasta board',
    body: 'The channel\'s long-form copypasta — the multi-sentence repost-pastas that the Lexicon\'s short list buries — now has its own page. Near-duplicate wordings of the same pasta are clustered into a single ranked entry (the festive-carpet family alone spans 18 variants), searchable and sortable, each expandable to its full text.',
    link: '?copypasta',
    spotlight: false,
  },
  {
    id: 'swing-grade-2026-06',
    date: 'Jun 24, 2026',
    emoji: '💥',
    title: 'Unit grades now weigh raw swing',
    body: 'The letter grade on each unit now accounts for basic-attack burst — how hard a single normal swing actually lands — which the old grade under-counted, and it properly counts both hands on a Dual Wielder. A clean, if marginal, signal that nudges heavy-hitting bruisers up a notch.',
    spotlight: false,
  },
  {
    id: 'wiznaibot-status-2026-06',
    date: 'Jun 20, 2026',
    emoji: '🤖',
    title: 'Wiznaibot Status — watch the bot bet',
    body: 'A new page following Wiznaibot\'s automated betting bot in real time: its live bankroll and net winnings, every bet it places with the model\'s reasoning behind it, and how its picks are actually doing — the model\'s pick tracked separately from the bet it places, with voided bets marked instead of stuck "pending".',
    link: '?wiznaibot',
  },
  {
    id: 'chat-section-2026-06',
    date: 'Jun 19, 2026',
    emoji: '💬',
    title: 'Chat, mined — six years of the stream\'s conversation',
    body: 'A whole new Chat section, built offline from ~42 million lines of the stream\'s Twitch chat. Chat Activity charts daily volume across six years — the 2020 viral peak settling into today\'s steady core — and ranks the most active chatters, by year or all-time. The Social Graph maps who @mentions whom, surfacing friend clusters and the biggest running feuds (Mushufasa_ vs NovaKnight21 leads at 1,560 exchanges). The Lexicon is a museum of the channel\'s emotes and copypasta — first-seen dates and rise-and-fall sparklines, so you can watch SourPls die and NODDERS take over. What Chat Forces breaks down 4.1 million !fight submissions: the classes and skills chat demands most, set against their real win rates (chat loves Knight and Calculator despite losing records). And an auto-built FAQ answers the questions chat actually asks. Every player profile also gains a 💬 Chat tab — their persona, tenure, catchphrase, top emotes, go-to build, and arch-rival.',
    link: '?chat',
  },
  {
    id: 'combat-sim-overhaul-2026-06',
    date: 'Jun 18, 2026',
    emoji: '⚔️',
    title: 'The battle simulator, brought up to speed',
    body: 'The Monte-Carlo battle sim behind every win-% — the ⚔ Simulate view, the bracket\'s % odds, and the Predict-the-Bracket favorite — got a deep fidelity pass against the Battle Mechanics Guide. Monsters now fight with their real intrinsic skills instead of just pecking (breath weapons, elemental souls, the Hydra triple-casts, Hurricane), and undead reanimate about half the time instead of crystallizing — while a Phoenix Down to the face kills them. Casters are interruptible: a unit mid-charge can\'t dodge and takes extra damage, Haste no longer shortens a cast, and Short Charge does. Whole skillsets that used to do nothing now connect — HP/MP drains (Night Sword), the Demi/Lich percent-HP nukes, Jump, Charge and Throw — alongside reactions like Dragon Spirit, HP Restore, Meatbone Slash and Arrow Guard. Critical hits, faith-gated magic (Innocent shrugs it off), and Slow / Silence / Confusion all matter now, and the AI commits to the fight like the real game instead of over-kiting. And the bracket and Predict odds now play out on each round\'s real battle map — its height, walls and chokepoints — instead of an abstract flat field. All gated against thousands of historical brackets so the odds stay honest.',
    link: '?mechanics',
  },
  {
    id: 'attendance-streaks-2026-06',
    date: 'Jun 17, 2026',
    emoji: '🔁',
    title: 'Attendance Streaks — who never misses',
    body: 'New boards under Players ▸ 📊 Boards: the longest run of consecutive tournaments a player ever took part in, counted three ways — in the bracket (any team, including holding the belt as champion), as a challenger (drafted onto a colored team, so a long title reign can\'t bridge a sleep gap), and "typed !fight" (drafted or snubbed — showed up and entered every single time). Memes are skipped, so they never break a streak, and any name links to that player\'s full profile.',
    link: '?players&tab=boards',
  },
  {
    id: 'sprite-gallery-2026-06',
    date: 'Jun 16, 2026',
    emoji: '🎞️',
    title: 'Sprite Gallery — every unit, animated',
    body: 'A new Sprites page renders every FFT battle sprite straight off the disc — all the human jobs and monsters — animated: walk, attack, hurt and death, plus cast and dance for casters and performers, viewable from the front or the back. Each one recolors live through its 16 palettes, so you can flip a monster between its family tiers (Bomb → Grenade → Explosive) or a job between team colors, dial the zoom and box size, and export any pose as an animated GIF.',
    link: '?gallery',
  },
  {
    id: 'early-champs-2026-06',
    date: 'Jun 12, 2026',
    emoji: '📜',
    title: 'Champion history now reaches Day One',
    body: 'The first month of FFTBG championship history — 389 reigns from Dec 29, 2019 to Jan 31, 2020, before full tournament records existed — has been recovered from the champion archive and merged in. The Line of Succession now traces back to the very first champion (a 13-defense blue-team run), and every early title now counts toward player championships, Monster Quest dates, droughts, and the class index.',
    link: '?crown',
  },
  {
    id: 'live-player-profile-2026-06',
    date: 'Jun 11, 2026',
    emoji: '🎁',
    title: 'Live profiles: last unit + skill collection',
    body: 'Player profiles now pull live data straight from FFTBG. A "Last fielded" card shows each player\'s most recent tournament unit — with an Open in Builder button to remix it — and a new 🎁 Skills tab lays out their full !buyrandom collection: owned vs missing, rarity-colored, with the exact priority modifier every owned skill is applying (including the masked "Hidden Skill" and prestige unlocks). Set your handle in settings and the bracket\'s skill-drop chip will also tell you whether you already own the current drop.',
    link: '?players',
  },
  {
    id: 'meta-tides-2026-06',
    date: 'Jun 11, 2026',
    emoji: '🌊',
    title: 'Meta Tides — watch the meta move',
    body: 'A new Stats view charting three years of meta history: stacked tides of each class\'s share of tournament rolls month by month (by archetype — click one to open it up — or top classes), and a bump chart ranking the quarterly win-rate leaders, where you can watch classes like Bard swing from #18 to #3 and back. Also works for Supports, Reactions, Skillsets, and weapon types.',
    link: '?stats&view=tides',
  },
  {
    id: 'predict-bracket-2026-06',
    date: 'Jun 10, 2026',
    emoji: '🔮',
    title: 'Predict-the-Bracket — can you beat the AI?',
    body: 'A new game on the live bracket: hit the 🔮 button, predict the winner of every match, and watch your score fill in as the tournament plays out — head-to-head against the battle sim. Pick on the bracket cells or right inside the ⚔ Fight View, fill the whole bracket upfront or just predict the rest, and build a lifetime record with a beat-the-sim streak. Picks save in your browser.',
    link: './',
  },
  {
    id: 'mechanics-page-2026-06',
    date: 'Jun 9, 2026',
    emoji: '📖',
    title: 'Battle Mechanics reference',
    body: 'A new page explaining how FFTBG\'s battles actually work — damage formulas and the modifier pipeline, zodiac compatibility, evasion, elements, Faith & Brave, turn order, plus full status and reaction/support references. Three live calculators (damage step-through, the zodiac matrix, and an evasion demo) run on the same code the unit cards use.',
    link: '?mechanics',
  },
  // ── Jun 2026 — the revamp ships ───────────────────────────────
  {
    id: 'crown-lineage-2026-06',
    date: 'Jun 9, 2026',
    emoji: '👑',
    title: 'Line of Succession',
    body: 'The complete crown lineage — every championship reign in order, newest first, with the team that dethroned each one. Filter by player, class, team color, or year. Find it under the new Champions menu, alongside the Tournament of Champions, Streaks, and Champions-by-class.',
    link: '?crown',
  },
  {
    id: 'player-compare-2026-06',
    date: 'Jun 9, 2026',
    emoji: '⚔️',
    title: 'Player vs Player comparison',
    body: 'Pick any two players and compare their full FFTBG careers side by side — win rates, championships, titles by year, signature classes, and a full class-by-class breakdown with diverging edge bars. Deep-linkable and swappable.',
    link: '?compare',
  },
  {
    id: 'hall-of-fame-2026-06',
    date: 'Jun 9, 2026',
    emoji: '🏛️',
    title: 'Hall of Fame',
    body: 'The 350+ Championship Club gets its own page — 12 superlative awards (Most Championships, Longest Reign, Ultimate Specialist, and more) plus a sortable full roster with an adjustable threshold. Find it under Players.',
    link: '?hall',
  },
  {
    id: 'builder-random-2026-06',
    date: 'Jun 9, 2026',
    emoji: '🎲',
    title: 'Random generator merged into Builder',
    body: 'The random unit generator now lives inside the Builder page — generate a random unit and immediately tweak every field. The 🎲 bracket button links straight to it.',
    link: '?build&random',
  },
  {
    id: 'active-droughts-2026-06',
    date: 'Jun 8, 2026',
    emoji: '🏜️',
    title: 'Active droughts on leaderboards',
    body: 'Ongoing streaks now appear alongside completed droughts. "Drought to first" tracks players who\'ve rolled a class 10+ times without ever champing it; "drought gap" tracks 10+ rolls since their last title. Active entries show in italics with a tag so you can watch them climb.',
    link: '?players&tab=droughts',
    spotlight: false,
  },
  {
    id: 'equip-shield-fix-2026-06',
    date: 'Jun 8, 2026',
    emoji: '🛡️',
    title: 'Equip Shield + Builder fixes',
    body: 'Equip Shield now correctly unlocks offhand shields in the Builder and gear editor. Mimes with Equip Shield can dual-wield shields — the evasion math handles both (FFT\'s multiplicative formula: two Aegis Shields = 55% magic evade, not 66%). Non-Mime classes still get the standard offhand-only rule.',
    link: '?build',
  },
  {
    id: 'player-hover-2026-06',
    date: 'Jun 8, 2026',
    emoji: '👤',
    title: 'Player hover cards',
    body: 'Hover any player name on the bracket for a quick career snapshot — win rate, games played, bracket wins, championships + title defenses, and their top 4 classes by games. Click to jump straight to their full profile page.',
    spotlight: false,
  },
  {
    id: 'class-droughts-2026-06',
    date: 'Jun 8, 2026',
    emoji: '🏜️',
    title: 'Class Droughts',
    body: 'How many rolls did it take before your first title with a class? What\'s the longest gap between titles? A dedicated Droughts tab on the Players page with per-class leaderboards (drought-to-first and longest gap), duration in days, class headshot icons, and drought stats on every player\'s profile.',
    link: '?players&tab=droughts',
  },
  {
    id: 'goal-tracker-2026-06',
    date: 'Jun 6, 2026',
    emoji: '🎯',
    title: 'Goal tracker',
    body: 'Pin any quest or achievement as your active goal from your profile page. A persistent widget on the bracket page shows what you still need and checks off progress as you play. Dismissible per-session, re-settable any time.',
  },
  {
    id: 'player-profile-2026-06',
    date: 'Jun 5, 2026',
    emoji: '👤',
    title: 'Player Profile page',
    body: 'A dedicated page for every player — tabbed into Overview (career stats, win rates by class), Achievements (tiered badges for milestones, quests, betting feats), History (championship timeline, monthly title chart, zodiac wheel, longest reign), and Gambling (balance, ROI, biggest swings). Set your username in Settings for a quick-access button.',
    link: '?players',
  },
  {
    id: 'win-rate-highlighting-2026-06',
    date: 'Jun 4, 2026',
    emoji: '🎨',
    title: 'Win-rate highlighting',
    body: 'Skills, abilities, and stats on the unit card are colored by their empirical win rate — green for above average, red for below. Baselines are gender-aware and class-relative, so a Mediator\'s 400 HP reads differently than a Knight\'s. Covers secondary skillsets, support/reaction/move skills, individual ability names, and the six core stats.',
    spotlight: false,
  },
  {
    id: 'onboarding-2026-06',
    date: 'Jun 3, 2026',
    emoji: '👋',
    title: 'Welcome tour + feature spotlights',
    body: 'First-time visitors get a step-by-step guided tour of the bracket page — teams, unit cards, the fight view, and the nav bar. Returning users see a one-time spotlight popup when new features ship. Both are tracked in localStorage so they only fire once.',
    spotlight: false,
  },
  {
    id: 'strategy-2026-06',
    date: 'Jun 2, 2026',
    emoji: '🧭',
    title: 'Strategy page',
    body: 'Per-class best and worst skill picks derived from 6 years of empirical win-rate data. Covers every forceable slot — secondary skillset, support, reaction, move, and accessory. A Humans tab with per-class strategy cards (click for the full breakdown) and a Monsters tab with the rotation guide. Only suggests picks that are actual stream tokens you can force.',
    link: '?players&tab=strategy',
    spotlight: false,
  },
  {
    id: 'memes-2026-06',
    date: 'Jun 2, 2026',
    emoji: '🃏',
    title: 'Meme Tournaments page',
    body: 'A filterable history of ~758 non-standard events — ToC brackets (auto-detected, including preshows), Algus mode, Halloween, April Fools, and unclassified meme rulesets. Bucket filters, a meme-type guide, champion-vs-winner rosters, and link-out per tournament. These are excluded from every stat computation so normal play stays clean.',
    link: '?memes',
    spotlight: false,
  },
  {
    id: 'site-nav-2026-06',
    date: 'Jun 2, 2026',
    emoji: '🗂️',
    title: 'Site-wide navigation bar',
    body: 'A persistent tab bar across every page — Bracket, Champions, Players, Maps, Gear Lab, Builder, Memes, Stats — with active-tab highlighting and a Players mega-menu for quick access to sub-tabs. Collapses to a hamburger menu on mobile. The bracket page renders its own nav variant with action buttons (settings, help, generator, prev-tournament).',
    spotlight: false,
  },
  {
    id: 'sim-calibration-2026-06',
    date: 'Jun 2, 2026',
    emoji: '📐',
    title: 'Sim probability calibration',
    body: 'Raw sim win rates were wildly overconfident — a 60/40 matchup would show as 85%. Now Platt-calibrated against ~2,400 real matches with a train/test split, bringing the Brier score from 0.33 to 0.25. A separate UI scaling factor keeps the displayed odds readable (clear favorites still read ~65-75%) while the underlying math stays honest.',
    spotlight: false,
  },
  {
    id: 'builder-2026-06',
    date: 'Jun 1, 2026',
    emoji: '🔧',
    title: 'Character Builder',
    body: 'Build a unit from scratch — pick class, gender, zodiac, Brave/Faith, skills, and gear — with a live unit card that updates as you go (damage formulas, evasion, elements, all of it). Export in FFTBG stream format for pasting, import from a paste, or share via a URL (?build&u=...). Gear options respect class legality and Equip-X support skills.',
    link: '?build',
    spotlight: false,
  },
  {
    id: 'gear-lab-2026-06',
    date: 'Jun 1, 2026',
    emoji: '🔍',
    title: 'Gear Lab',
    body: 'Pick an item (or a pair) and explore its history across ~2.3 million units. Per-slot facets show how often it rolls, its win rate, top class, and example players. Two-item search finds co-equipped combos and how they perform together. Browse items by popularity with the fancy picker, or deep-link with ?gear&a=Slot:Item.',
    link: '?gear',
    spotlight: false,
  },
  // ── Jun 2026 — Maps ───────────────────────────────────────────
  {
    id: 'maps-psx-mesh-2026-06',
    date: 'Jun 2, 2026',
    emoji: '🗺️',
    title: 'Real PSX map meshes',
    body: 'All 121 battle maps now render their actual PlayStation geometry — meshes and textures extracted straight from the disc image, with transparency and palette fixes for the tricky ones (Death City, Chocobo Forest, etc.). Viewable in the 3D viewer on each map\'s detail page.',
    link: '?maps',
    spotlight: false,
  },
  {
    id: 'maps-page-2026-06',
    date: 'Jun 1, 2026',
    emoji: '🗺️',
    title: 'Maps page',
    body: 'A searchable grid of all 138 battle maps with empirical class win-rate profiles (which classes a map favors or hurts), terrain height grids, a 3D viewer, starting positions from the game\'s ENTD binary, Calculator height-targeting analysis, and start-side balance (the left/right team advantage per map). Built from ~65k tournaments of QF match data. Click any card for the full detail page with sortable class/skill tables.',
    link: '?maps',
    spotlight: false,
  },
  // ── Jun 2026 — Sim ────────────────────────────────────────────
  {
    id: 'sim-terrain-2026-06',
    date: 'Jun 2, 2026',
    emoji: '⚔️',
    title: 'Battle sim on real terrain',
    body: 'The Monte Carlo sim now runs on real map geometry — true width/height grids with walls, void tiles, elevation, and jump-aware pathfinding. Starting positions use the authoritative ENTD data. The battle replay viewer renders the actual terrain with elevation shading and height numbers so you can watch fights play out on the real battlefield.',
    spotlight: false,
  },
  {
    id: 'sim-realism-2026-06',
    date: 'Jun 1, 2026',
    emoji: '⚔️',
    title: 'Battle simulator',
    body: 'A full Monte Carlo team-vs-team combat sim faithful to FFT mechanics. 600 runs per matchup with facing-aware evasion, charge-time spells, reactions (Counter/Hamedo/Blade Grasp/Auto-Potion), friendly-fire AoE, mimes that echo allies, linear attacks, dual wield, status effects (Charm/Berserk/Frog/Darkness/Reflect and more), zodiac compatibility, elemental affinity, and a greedy AI that flanks, kites, and prioritizes casters.',
    spotlight: false,
  },
  // ── May 2026 ──────────────────────────────────────────────────
  {
    id: 'sounds-2026-05',
    date: 'May 2026',
    emoji: '🔊',
    title: 'Notification sounds',
    body: 'Pure Web Audio synth alerts (no downloaded assets) when your highlighted players\' units win, lose, or become champion — plus a new-tournament ding. Three sound sets (chime, retro, bell) with five per-event toggles and preview buttons in Settings. Fires on the 60-second live poll, with snapshot tracking so already-decided matches stay silent.',
    spotlight: false,
  },
  {
    id: 'streaks-2026-05',
    date: 'May 2026',
    emoji: '🔥',
    title: 'Streaks + Greatest Dynasties',
    body: 'Longest championship reigns by class, and a Greatest Dynasties leaderboard — the longest unbroken title-defense runs in FFTBG history. Expandable rosters show the units that held the throne. Glitch-split reigns (where a meme tournament briefly interrupted a streak) are bridged so the real dynasty length is preserved.',
    link: '?players&tab=streaks',
    spotlight: false,
  },
  {
    id: 'betting-2026-05',
    date: 'May 2026',
    emoji: '💰',
    title: 'Betting leaderboards + profiles',
    body: 'Gambling stats for ~17k players — current balance, all-time ROI, win rate, biggest single win/loss, and balance-over-time charts. Sortable boards (richest, best ROI, most bets, biggest single win). Player names are case-folded and merged across account renames so careers track correctly.',
    link: '?players&tab=betting',
    spotlight: false,
  },
  {
    id: 'achievements-2026-05',
    date: 'May 2026',
    emoji: '🏅',
    title: 'Achievements',
    body: 'Tiered badges (bronze/silver/gold) for career milestones (games, wins, bracket wins), quest completions (Monster Quest, Human Quest, Everything Bagel, and more), betting feats, gendered championships, zodiac signs collected, and team colors won with. Shown on the player profile\'s Achievements tab.',
    spotlight: false,
  },
  {
    id: 'players-2026-05',
    date: 'May 2026',
    emoji: '👥',
    title: 'Players page',
    body: 'A searchable, sortable directory of ~17k players (min 5 games) across the full ~6-year history. Games, win rate, bracket wins, championships, per-class win-rate bars, and best/worst class. Tabs for Monster Quest progress (completions leaderboard, icon checklists, speedruns, Monster Families), Champions-by-class top-50 boards, and career boards (Old Guard, Day One, Fresh Blood). Player names are merged across account renames.',
    link: '?players',
    spotlight: false,
  },
  {
    id: 'champions-2026-05',
    date: 'May 2026',
    emoji: '🏆',
    title: 'Champions page',
    body: 'The current season\'s champion roster from the live API — a top-8 seeded bracket with on-demand Monte Carlo sim odds, a sortable leaderboard (rank, streak, team grade, expandable rosters), a season picker to browse past seasons, and a pinned Champion of Champions banner. Unit cards are fully interactive with damage formulas and gear details.',
    link: '?champions',
    spotlight: false,
  },
  {
    id: 'stats-v2-2026-05',
    date: 'May 2026',
    emoji: '📊',
    title: 'Stats panel v2',
    body: 'A complete rewrite of the stats panel. Windowed views (1 week / 1 month / 1 year / all), dimension picker with sortable tables, a skill-to-class drill-down (click any skill row to see its win rate broken down by class), a class filter (pick a class to see every skill\'s win rate for it), archetype splits (caster/melee/hybrid/monster), per-monster Brave/Faith curves, market favorite-rate by round, and the Class & Monster Index — a sortable table of all 76 types combining combat win rate with championship success. Replaced the old static stats grid entirely.',
    spotlight: false,
  },
  {
    id: 'fight-view-2026-05',
    date: 'May 2026',
    emoji: '⚔️',
    title: 'Fight view + damage matrix',
    body: 'Click any bracket cell to open the fight view. Pick an attacker from either team and see a matrix of their damaging abilities (plus basic Attack, Jump, status spells) against all four enemies — estimated damage, hit chance, and color-coded weak/half/absorb. Click a cell for the step-by-step breakdown. Target-status checkboxes (Protect/Shell/Charging/Sleeping/Frog/Oil) recompute on the fly. Estimated turn order up top. Dual Wield shows two Attack rows. Mimes list teammates\' abilities recomputed with the mime\'s own stats.',
    spotlight: false,
  },
  {
    id: 'unit-cards-2026-05',
    date: 'May 2026',
    emoji: '🃏',
    title: 'Revamped unit cards',
    body: 'Three-state unit cards: compact (portrait + one-line loadout summary) → detailed (click to expand in place) → full modal. Parser-driven damage formulas from the API info strings, colored skill rarity pills with roll percentages, directional evasion (front/side/back physical + magic), zodiac compatibility blocks per team, elemental affinity chips (strengthen/absorb/half/weak/cancel), and a damage-tuning panel with Brave/Faith sliders and a disarm toggle.',
    spotlight: false,
  },
  // ── The original site ─────────────────────────────────────────
  {
    id: 'original-site',
    date: '2020–2025',
    emoji: '🏗️',
    title: 'The original Wiznaib.us',
    body: 'The site that started it all — a live bracket viewer with team grids, per-unit stat cards, item/skill tooltips, basic damage estimates, the random character generator (with force/ban token support), highlighted users, tournament archive browsing (?tournament=ID), and the original stats table. Built while learning Vue, powered by the FFTBG and mustad.io APIs.',
    spotlight: false,
  },
];
